import React from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api } from '../api/client';
import { BackHeader } from '../components/BackHeader';
import {
  AlertTriangle,
  CheckCircle,
  Shield,
  Loader2,
  User,
} from 'lucide-react';
import { CertReviewBadge } from '../components/CertReviewBadge';
import { certTypeLabel } from '../lib/certTypes';
import { daysUntilCalendarDate, formatCalendarDate } from '../lib/calendar-date';
import { useTenantTimeZone } from '../hooks/useTenantTimeZone';

const TABS = [
  { key: 'expired', label: 'Expired', color: 'text-red-400', activeColor: 'border-red-400 text-red-400' },
  { key: 'expiring', label: 'Expiring', color: 'text-amber-400', activeColor: 'border-amber-400 text-amber-400' },
  { key: 'current', label: 'Current', color: 'text-green-400', activeColor: 'border-green-400 text-green-400' },
];

export default function TeamCertsScreen() {
  const [searchParams, setSearchParams] = useSearchParams();
  const status = searchParams.get('status') || 'expired';

  const { data, isLoading } = useQuery({
    queryKey: ['team-certs', status],
    queryFn: () => api.safety.teamCerts(status).then(r => r.data.data ?? r.data ?? []),
  });

  const certs: any[] = data ?? [];

  const formatDate = (d: string) =>
    formatCalendarDate(d, { month: 'short', day: 'numeric', year: 'numeric' }) ?? '—';

  // Days left counted from the TENANT's today, not the device's.
  const timeZone = useTenantTimeZone();
  const daysUntil = (d: string) => daysUntilCalendarDate(d, timeZone);

  return (
    <div className="animate-fade-in min-h-full bg-fieldiq-dark pb-[var(--bottom-chrome)]">
      <BackHeader to="/" label="Home" title="Team Certifications" />

      <div className="max-w-[1920px] mx-auto px-4 py-4 space-y-4">
        {/* Tabs */}
        <div className="flex border-b border-fieldiq-border">
          {TABS.map(tab => (
            <button
              key={tab.key}
              onClick={() => setSearchParams({ status: tab.key })}
              className={`flex-1 text-center py-2.5 text-sm font-semibold border-b-2 transition-colors ${
                status === tab.key
                  ? tab.activeColor
                  : 'border-transparent text-fieldiq-muted hover:text-white'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Count */}
        <p className="text-xs text-fieldiq-muted">{certs.length} certification{certs.length !== 1 ? 's' : ''}</p>

        {/* Loading */}
        {isLoading && (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-6 h-6 text-fieldiq-accent animate-spin" />
          </div>
        )}

        {/* Empty */}
        {!isLoading && certs.length === 0 && (
          <div className="text-center py-12">
            <Shield size={40} className="mx-auto text-fieldiq-muted mb-3" />
            <p className="text-fieldiq-text">No {status} certifications found.</p>
          </div>
        )}

        {/* List */}
        {!isLoading && certs.length > 0 && (
          <div className="space-y-3">
            {certs.map((cert: any) => {
              const days = daysUntil(cert.expiry_date);
              const isExpired = days !== null && days <= 0;
              const isExpiring = days !== null && days > 0 && days <= 30;

              return (
                <div
                  key={cert.id}
                  className="bg-fieldiq-card rounded-xl border border-fieldiq-border p-4"
                >
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-full bg-fieldiq-dark flex items-center justify-center flex-shrink-0">
                      <User size={18} className="text-fieldiq-muted" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between">
                        <h3 className="text-sm font-bold text-white">
                          {cert.first_name} {cert.last_name}
                        </h3>
                        <div className="flex items-center gap-1.5">
                          <CertReviewBadge status={cert.review_status} />
                          {isExpired ? (
                            <span className="text-[10px] px-2 py-0.5 rounded-full bg-red-500/20 text-red-400 font-semibold">
                              Expired
                            </span>
                          ) : isExpiring ? (
                            <span className="text-[10px] px-2 py-0.5 rounded-full bg-amber-500/20 text-amber-400 font-semibold">
                              {days}d left
                            </span>
                          ) : (
                            <span className="text-[10px] px-2 py-0.5 rounded-full bg-green-500/20 text-green-400 font-semibold flex items-center gap-1">
                              <CheckCircle size={10} /> Current
                            </span>
                          )}
                        </div>
                      </div>
                      <p className="text-xs text-fieldiq-accent mt-0.5">
                        {/* The shared label, so a trucking licence row (api#1105) reads
                          * "Driver's Licence" rather than the raw key. */}
                        {certTypeLabel(cert.certification_type || cert.cert_type)}
                        {cert.custom_type ? ` — ${cert.custom_type}` : ''}
                      </p>
                      {cert.role && (
                        <p className="text-xs text-fieldiq-muted mt-0.5 capitalize">{cert.role.replace(/_/g, ' ')}</p>
                      )}
                      <div className="flex items-center gap-4 mt-2 text-xs text-fieldiq-muted">
                        {(cert.certificate_number || cert.cert_number) && <span>#{cert.certificate_number || cert.cert_number}</span>}
                        {(cert.issuing_body || cert.issuer) && <span>{cert.issuing_body || cert.issuer}</span>}
                        {cert.review_version > 1 && <span>v{cert.review_version}</span>}
                      </div>
                      {cert.tech_name && (
                        <p className="text-[10px] text-fieldiq-muted mt-1">Submitted by {cert.tech_name}</p>
                      )}
                      <div className="flex items-center gap-4 mt-1 text-xs">
                        {(cert.issued_date || cert.issue_date) && (
                          <span className="text-fieldiq-muted">Issued: {formatDate(cert.issued_date || cert.issue_date)}</span>
                        )}
                        <span className={isExpired ? 'text-red-400' : isExpiring ? 'text-amber-400' : 'text-fieldiq-muted'}>
                          {isExpired ? (
                            <span className="flex items-center gap-1"><AlertTriangle size={10} /> Expired: {formatDate(cert.expiry_date)}</span>
                          ) : (
                            `Expires: ${formatDate(cert.expiry_date)}`
                          )}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
